import { Quill } from "react-quill-new";

type QuillLine = {
    domNode?: Node;
    offset?: (scroll: unknown) => number;
    length?: () => number;
};

const cellOf = (node: Node | null | undefined): HTMLElement | null => {
    if (!node) return null;
    const el = node instanceof Element ? node : node.parentElement;
    return el?.closest("td, th") ?? null;
};

const lineAt = (quill: Quill, index: number): QuillLine | null => {
    try {
        const [line] = quill.getLine(index) as [QuillLine | null, number];
        return line ?? null;
    } catch {
        return null;
    }
};

/** Index inside the line, before its trailing newline. Past the line → end of its content. */
const clampInsideLine = (quill: Quill, line: QuillLine, preferred: number): number | null => {
    if (typeof line.offset !== "function" || typeof line.length !== "function") return null;
    const start = line.offset(quill.scroll);
    if (!Number.isFinite(start)) return null;
    const end = start + Math.max(0, line.length() - 1);
    if (preferred >= start && preferred <= end) return preferred;
    return end;
};

const findCellBlock = (cell: HTMLElement): QuillLine | null => {
    const el = cell.querySelector(
        "p.ql-table-block, p.table-th-block, .ql-table-header, .table-list, p, h1, h2, h3, h4, h5, h6, li",
    );
    if (!(el instanceof HTMLElement)) return null;
    return (Quill.find(el) as QuillLine | null) ?? null;
};

const collectImages = (root: ParentNode): HTMLImageElement[] => {
    const nodes = root.querySelectorAll("img");
    const images: HTMLImageElement[] = [];
    for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes.item(i);
        if (node) images.push(node);
    }
    return images;
};

const imagesAddedSince = (root: ParentNode, before: HTMLImageElement[]): HTMLImageElement[] =>
    collectImages(root).filter((img) => before.indexOf(img) < 0);

const imageIndex = (quill: Quill, img: HTMLImageElement): number | null => {
    const blot = Quill.find(img);
    if (!blot) return null;
    try {
        return quill.getIndex(blot as never);
    } catch {
        return null;
    }
};

const removeImage = (quill: Quill, img: HTMLImageElement) => {
    const index = imageIndex(quill, img);
    if (index != null) {
        quill.deleteText(index, 1, "user");
        return;
    }
    img.remove();
};

/**
 * Insert an image embed at the saved cursor.
 * Inside a table cell the index stays in the cell paragraph: a direct child of `td`
 * is dropped by quill-table-better.
 * Returns the cursor index after the image.
 */
export const insertEditorImage = (quill: Quill, imageSrc: string, preferredIndex?: number): number => {
    const docEnd = Math.max(0, quill.getLength() - 1);
    const raw = preferredIndex ?? quill.getSelection()?.index ?? docEnd;
    const index = Math.min(Math.max(0, raw), docEnd);
    if (!imageSrc) return index;

    const line = lineAt(quill, index);
    const lineInCell = line?.domNode ? cellOf(line.domNode) : null;
    const cell =
        lineInCell ??
        quill.root.querySelector<HTMLElement>("td.ql-cell-focused, th.ql-cell-focused");

    let at = index;
    if (cell && line && lineInCell === cell) {
        const clamped = clampInsideLine(quill, line, index);
        if (clamped != null) at = clamped;
    } else if (cell) {
        const block = findCellBlock(cell);
        const inner = block ? clampInsideLine(quill, block, index) : null;
        if (inner != null) at = inner;
    }

    const before = collectImages(quill.root);
    quill.insertEmbed(at, "image", imageSrc, "user");
    let added = imagesAddedSince(quill.root, before);
    const landed = cell ? added.find((img) => cell.contains(img)) : added[0];

    if (cell && !landed) {
        added.forEach((img) => removeImage(quill, img));
        const block =
            (line?.domNode && cell.contains(line.domNode) ? line : null) ?? findCellBlock(cell);
        const inner = block ? clampInsideLine(quill, block, at) : null;
        if (inner != null) {
            const beforeRetry = collectImages(quill.root);
            quill.insertEmbed(inner, "image", imageSrc, "user");
            added = imagesAddedSince(quill.root, beforeRetry);
            const placed = added.find((img) => cell.contains(img)) || added[0];
            at = (placed ? imageIndex(quill, placed) : null) ?? inner;
        }
    } else if (landed) {
        const landedAt = imageIndex(quill, landed);
        if (landedAt != null) at = landedAt;
    }

    const cursor = at + 1;
    quill.setSelection(cursor, 0, "user");
    return cursor;
};
